import { useState } from "react";

import { fallbackImage } from "components/Movie/utils";
import { useShowMovieDetails } from "hooks/reactQuery/useMoviesApi";
import { Rating, RatingFilled } from "neetoicons";
import { Typography, Tag, Spinner, NoData, Button } from "neetoui";
import { useTranslation } from "react-i18next";
import { useFavoriteStore } from "stores/useFavoriteStore";
import { camelCaseToTitleCase } from "utils/camelCaseToTitleCase";

const Details = ({ movieId }) => {
  const { t } = useTranslation();
  const { checkIsFavorite, addToFavorites, removeFromFavorites } =
    useFavoriteStore.pick();
  const [isFavorite, setIsFavorite] = useState(checkIsFavorite(movieId));
  const {
    data: {
      title,
      genre,
      plot,
      poster,
      director,
      actors,
      boxOffice,
      year,
      runtime,
      language,
      rated,
      imdbRating: rating,
    } = {},
    isError,
    isFetching,
  } = useShowMovieDetails(movieId);

  const movieDisplayDetails = Object.entries({
    director,
    actors,
    boxOffice,
    year,
    runtime,
    language,
    rated,
  });

  const genreTags = genre?.split(",").map(genre => genre.trim());

  const handleAddToFavorites = () => {
    if (!isFavorite) {
      const movieObject = {
        title,
        imdbId: movieId,
        rating,
      };

      addToFavorites(movieObject);
      setIsFavorite(true);
    }
  };

  const handleRemoveFromFavorites = () => {
    if (isFavorite) {
      removeFromFavorites(movieId);
      setIsFavorite(false);
    }
  };
  if (isFetching) {
    return (
      <div className="flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="w-full py-10 text-center">
        <NoData title={t("errors.someThingWentWrong")} />
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-col justify-center gap-2">
        <Typography
          className="flex items-center space-x-4"
          style="h2"
          weight="bold"
        >
          {title}
          <Button
            className="mx-4 rounded-full"
            icon={isFavorite ? RatingFilled : Rating}
            style="text"
            tooltipProps={{
              content: !isFavorite
                ? t("message.addToFavorites")
                : t("message.removeFromFavorites"),
              position: "right",
            }}
            onClick={
              isFavorite ? handleRemoveFromFavorites : handleAddToFavorites
            }
          />
        </Typography>
        <div className="flex flex-wrap gap-2">
          {genreTags?.map((tag, index) => (
            <Tag className="capitalize" key={index} label={tag} />
          ))}
        </div>
      </div>
      <div>
        <div className="flex flex-col gap-6 md:flex-row">
          <img
            alt={title}
            className="max-h-[450px] w-full rounded-xl object-contain shadow md:w-1/3"
            src={poster}
            onError={fallbackImage}
          />
          <div className="flex flex-col gap-2 md:w-2/3">
            <Typography className="text-gray-700" component="i" style="body1">
              {plot}
            </Typography>
            <div className="mt-4 space-y-1">
              {movieDisplayDetails.map(([key, value]) => (
                <Typography key={key} style="body2">
                  <strong>{camelCaseToTitleCase(key)}:</strong> {value}
                </Typography>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Details;
